import { useState, useEffect } from "react"

import { Box, Stack } from "@mui/material"
import { markerDataFromEvents, remapRawServerEvents } from "@/common/utils"
import GoogleMapComponent from "@/components/mapBox/GoogleMapComponent"
import { useJsApiLoader } from "@react-google-maps/api"
import { useEventContext } from "@/contexts/EventContext"
import useGetMapEvents from "@/hooks/api/events/useGetMapEvents"
import { useQueryClient } from "@tanstack/react-query"
import queryKeys from "@/api/queryKeys"
import { RawServerEvent, MarkerData } from "@/common/types"
import { useLocation } from "react-router-dom"
import LeftAdvertisements from "../advertisements/LeftAdvertisements"
import RightAdvertisements from "../advertisements/RightAdvertisements"
import useIsTablet from "@/hooks/useIsTablet"

const mapLibraries: ["places"] = ["places"] // define libraries as a constant outside the component

const MapComponent = () => {
  const isTablet = useIsTablet()
  const queryClient = useQueryClient()
  const [markerData, setMarkerData] = useState<MarkerData[]>([] as MarkerData[])
  const [bounds, setBounds] = useState<{
    north: number
    south: number
    east: number
    west: number
  }>({
    north: 32.9,
    south: 32.6,
    east: -79.8,
    west: -80.1,
  })

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_KEY,
    libraries: mapLibraries,
  })

  const { value, searchVal, petFriendlyFilter, ageRangeFilter, priceFilter } =
    useEventContext()

  const { data: eventResponse, isSuccess } = useGetMapEvents({
    filters: {
      filter: value,
      searchValue: searchVal,
      petFriendly: petFriendlyFilter,
      ageRange: ageRangeFilter,
      price: priceFilter,
      bounds,
    },
  })

  useEffect(() => {
    if (!bounds) return
    queryClient.invalidateQueries({
      queryKey: queryKeys.events.mapPage({
        searchValue: searchVal,
        filter: value,
        petFriendly: petFriendlyFilter,
        ageRange: ageRangeFilter,
        price: priceFilter,
        bounds,
      }).queryKey,
    })
    if (isSuccess && eventResponse) {
      setMarkerData(
        markerDataFromEvents(
          remapRawServerEvents(eventResponse.events as RawServerEvent[])
        ) as MarkerData[]
      )
    }
  }, [
    queryClient,
    searchVal,
    value,
    petFriendlyFilter,
    ageRangeFilter,
    priceFilter,
    eventResponse,
    isSuccess,
    bounds,
  ])

  const location = useLocation()

  return (
    <>
      <Box
        className="theme-bluecontainer"
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          paddingTop: "2rem",
          paddingBottom: "2rem",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "center",
            width: "100%",
            height: "720px",
            px: isTablet ? "1rem" : "2rem",
            flexWrap: "nowrap",
          }}
        >
          {!isTablet && <LeftAdvertisements />}
          <Box
            style={{
              height: "650px",
              width: "100%",
              maxWidth: "920px",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            {!isLoaded ? (
              <div>Loading...</div>
            ) : (
              <GoogleMapComponent
                markerData={markerData}
                location={location}
                setBounds={setBounds}
                activeFilters={value}
              />
            )}
          </Box>
          <RightAdvertisements />
        </Stack>
      </Box>
    </>
  )
}

export default MapComponent
